// Link-earning reference resources — long-form, citable material designed to
// attract editorial backlinks from architects, developers, integrators and press.
// Single source of truth for /resources, /resources/$slug, sitemap and markdown mirrors.

import solPerimeter from "@/assets/sol-perimeter.jpg";
import solAutomation from "@/assets/sol-automation.jpg";
import solCinema from "@/assets/sol-cinema.jpg";
import solCctv from "@/assets/sol-cctv.jpg";
import solAuditorium from "@/assets/sol-auditorium.jpg";

export type ResourceTable = { caption?: string; columns: string[]; rows: string[][] };

export type ResourceSection = {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
  table?: ResourceTable;
};

export type ResourceTakeaway = {
  /** Headline number or rule a journalist / AI answer can quote verbatim. */
  metric: string;
  /** One-sentence, citation-ready statement of the benchmark. */
  point: string;
  /** Heading of the section that carries the supporting table or checklist. */
  source: string;
  /** Question text of the FAQ entry that restates the benchmark in prose. */
  faq: string;
};

export type Resource = {
  slug: string;
  title: string;          // page <h1>
  metaTitle: string;      // <title>
  description: string;    // meta description
  eyebrow: string;
  summary: string;        // hero paragraph
  image: string;          // relative asset path
  updated: string;        // ISO date
  readTime: string;
  keyFacts: { label: string; value: string }[];
  /** Citation-ready summary of the page's benchmarks, linked to their evidence. */
  takeaways: ResourceTakeaway[];
  sections: ResourceSection[];
  faqs: { q: string; a: string }[];
  citation: string;
  related: { to: string; label: string }[];
};

/** Stable anchor id for a resource section heading. */
export function sectionAnchor(heading: string): string {
  return heading
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/** Stable anchor id for a resource FAQ question. */
export function faqAnchor(question: string): string {
  return `faq-${sectionAnchor(question)}`;
}

export const RESOURCES: Resource[] = [
  {
    slug: "perimeter-protection-benchmarks",
    metaTitle: "Perimeter Protection Benchmarks | Design Reference",
    title: "Perimeter Protection Design Benchmarks",
    description:
      "Field-tested design benchmarks for perimeter protection — detection layers, nuisance-alarm targets, camera-to-sensor ratios, response times and commissioning tests.",
    eyebrow: "Reference · Perimeter Protection",
    summary:
      "A working reference for specifying perimeter intrusion detection on estates, campuses, plants and infrastructure sites — written from over 500 km of perimeter designed and commissioned across India, not from vendor datasheets.",
    image: solPerimeter,
    updated: "2026-08-11",
    readTime: "12 min",
    keyFacts: [
      { label: "Perimeter designed & commissioned", value: "500+ km" },
      { label: "Practice experience", value: "23+ years" },
      { label: "Detection layers recommended", value: "3 (detect · assess · delay)" },
    ],
    takeaways: [
      {
        metric: "Pd ≥ 95%",
        point:
          "Specify probability of detection at 95% or better across ten walk, climb and crawl trials per zone — a single witnessed demo hides blind spots at posts and corners.",
        source: "Acceptance benchmarks to write into a specification",
        faq: "How should perimeter performance be verified at handover?",
      },
      {
        metric: "≤ 1 nuisance alarm / zone / 24 h",
        point:
          "After a 14-day tuning period, a perimeter should generate no more than one nuisance alarm per zone per day, and no more than two weather- or animal-driven alarms per zone per week through a monsoon cycle.",
        source: "Acceptance benchmarks to write into a specification",
        faq: "What is an acceptable nuisance alarm rate for a perimeter system?",
      },
      {
        metric: "≤ 100 m fence zones",
        point:
          "Keep fence-mounted detection zones at or under 100 m and buried or radar zones at or under 200 m, and never longer than one assessment camera can resolve.",
        source: "Acceptance benchmarks to write into a specification",
        faq: "How long should a perimeter detection zone be?",
      },
      {
        metric: "≥ 50 px/m at the fence line",
        point:
          "Assessment cameras must cover 100% of every zone at 50 pixels per metre or better, with alarm-to-video call-up inside 2 seconds, or the operator cannot classify person versus animal.",
        source: "Acceptance benchmarks to write into a specification",
        faq: "Is thermal imaging enough on its own for perimeter protection?",
      },
      {
        metric: "3 layers: detect · assess · delay",
        point:
          "Detection, assessment and delay are separate jobs; thermal imaging is an assessment layer and should not be sold as the only detection layer on a fenced boundary.",
        source: "The three-layer model",
        faq: "Is thermal imaging enough on its own for perimeter protection?",
      },
    ],
    sections: [
      {
        heading: "What a perimeter benchmark is for",
        paragraphs: [
          "Most perimeter tenders are written around a product — a fence sensor, a thermal camera, a radar. The result is a system that passes a factory demo and fails a monsoon night. A benchmark reverses the order: define the detection performance the site needs, then let technology compete against it.",
          "The benchmarks below are the acceptance targets we write into specifications. They are deliberately technology-agnostic: any vendor may meet them with any technology, provided the numbers hold during a witnessed site test.",
        ],
      },
      {
        heading: "The three-layer model",
        bullets: [
          "Detect — the sensing layer that declares an event (fence sensor, buried cable, microwave, radar, thermal analytics).",
          "Assess — the visual layer that lets an operator classify the event within seconds (fixed camera coverage plus a slaved PTZ or thermal).",
          "Delay & respond — the physical and procedural layer (fence type, topping, lighting, patrol route, SOP) that buys time for the response.",
          "A perimeter that has detection without assessment produces alarm fatigue. Assessment without delay produces recorded intrusions.",
        ],
      },
      {
        heading: "Acceptance benchmarks to write into a specification",
        table: {
          caption: "Targets used as witnessed-test acceptance criteria on Indian sites.",
          columns: ["Parameter", "Benchmark to specify", "Why it matters"],
          rows: [
            ["Probability of detection (Pd)", "≥ 95% across 10 walk/climb/crawl trials per zone", "Single-trial demos hide blind spots at posts and corners"],
            ["Nuisance alarm rate", "≤ 1 per zone per 24 h after 14-day tuning", "Above this, operators start acknowledging without assessing"],
            ["False alarm rate (weather/animal)", "≤ 2 per zone per week through one monsoon cycle", "Rain, wind and stray dogs are the real test, not a clear evening"],
            ["Zone length", "≤ 100 m (fence-mounted), ≤ 200 m (buried/radar)", "Longer zones make assessment cameras useless"],
            ["Assessment camera coverage", "100% of every zone, ≥ 50 px/m at the fence line", "Below this an operator cannot classify person vs animal"],
            ["Alarm-to-video latency", "≤ 2 s from sensor trip to camera call-up", "Beyond 2 s the intruder has left the frame"],
            ["Assessment time", "≤ 10 s operator decision, per SOP", "Defines how much delay the physical barrier must provide"],
            ["Lighting at fence line", "≥ 10 lux, or thermal in lieu", "Colour cameras without light are an unbudgeted retrofit"],
            ["Availability", "≥ 99% per zone per quarter, logged", "Turns AMC from a promise into a measurable"],
            ["Power & comms", "Fibre backbone, PoE with 4 h battery per node", "Perimeter fails first at the copper and the power"],
          ],
        },
      },
      {
        heading: "Choosing detection technology against site conditions",
        table: {
          columns: ["Technology", "Best fit", "Weak against"],
          rows: [
            ["Fence-mounted vibration sensor", "Existing chain-link / welded-mesh boundary, long runs", "Poor fence tension, high wind, loose panels"],
            ["Buried cable / seismic", "Aesthetic-sensitive estates, gaps between walls", "Waterlogging, heavy vehicular traffic nearby"],
            ["Microwave barrier", "Flat, clear, straight sectors", "Vegetation, standing water, undulating ground"],
            ["Ground-surveillance radar", "Wide open land, plants, substations, solar farms", "Dense clutter, tight urban plots"],
            ["Thermal + video analytics", "Assessment layer and open approaches", "Being sold as the only detection layer"],
            ["Electric / taut-wire fence", "High-threat industrial and infrastructure", "Residential and public-facing boundaries"],
          ],
        },
      },
      {
        heading: "Commissioning tests that should never be skipped",
        bullets: [
          "Ten-trial walk test per zone at three intrusion styles (climb, cut, crawl), witnessed and logged.",
          "Night test with all site lighting in its real, as-operated state.",
          "Monsoon or simulated-rain test before final acceptance, not after handover.",
          "Alarm flood test — trigger six zones at once and time operator handling.",
          "Comms failure test — cut a fibre leg and confirm the head end reports a fault, not silence.",
          "Ninety-day nuisance-alarm log review as the trigger for retention money release.",
        ],
      },
      {
        heading: "Common specification mistakes",
        bullets: [
          "Buying detection by kilometre price instead of by probability of detection.",
          "Zones longer than the assessment camera can cover.",
          "No tuning period written into the contract — so tuning never happens.",
          "Analytics licensed on a server that has no GPU headroom for the second phase.",
          "No SOP, so a validated alarm has no defined response.",
          "AMC scope that covers hardware replacement but not re-tuning after site changes.",
        ],
      },
    ],
    faqs: [
      {
        q: "What is an acceptable nuisance alarm rate for a perimeter system?",
        a: "As a working benchmark, no more than one nuisance alarm per zone per 24 hours after a two-week tuning period, and no more than two weather- or animal-driven alarms per zone per week through a monsoon cycle. Above that, operators stop assessing alarms and the system stops protecting the site.",
      },
      {
        q: "How long should a perimeter detection zone be?",
        a: "Keep fence-mounted zones at or under 100 m and buried or radar zones at or under 200 m, and only as long as one assessment camera can actually resolve the whole zone at roughly 50 pixels per metre at the fence line.",
      },
      {
        q: "Is thermal imaging enough on its own for perimeter protection?",
        a: "Rarely. Thermal is an excellent assessment and open-approach layer, but on a fenced boundary it should sit alongside a physical detection layer. Detection and assessment are different jobs and one device seldom does both to specification.",
      },
      {
        q: "How should perimeter performance be verified at handover?",
        a: "By witnessed testing, not by demonstration. Ten intrusion trials per zone across climb, cut and crawl, a night test under real site lighting, an alarm-flood test and a ninety-day nuisance-alarm log tied to retention release.",
      },
    ],
    citation:
      "Arif Khan, “Perimeter Protection Design Benchmarks”, arifkhanglobal.com, 2026.",
    related: [
      { to: "/solutions/perimeter-protection", label: "Perimeter Protection solution" },
      { to: "/solutions/cctv-command-centre", label: "CCTV & Command Centre" },
      { to: "/locations/security-consultant-delhi-ncr", label: "Security consultant — Delhi NCR" },
    ],
  },

  {
    slug: "smart-automation-playbook",
    metaTitle: "Smart Building Automation Playbook | Design Reference",
    title: "The Smart Automation Playbook",
    description:
      "A vendor-neutral playbook for smart home and building automation — scope order, protocol choices, cabling, control topology, commissioning and handover documentation.",
    eyebrow: "Reference · Smart Automation",
    summary:
      "How to plan automation so it still works in year five: the decision order, the protocol trade-offs, the cabling you cannot retrofit, and the handover pack that decides whether a system survives its first service contract.",
    image: solAutomation,
    updated: "2026-08-11",
    readTime: "11 min",
    keyFacts: [
      { label: "Practice experience", value: "23+ years" },
      { label: "Decisions locked at civil stage", value: "Cabling, containment, panel space" },
      { label: "Rule", value: "Design the operation, then the product" },
    ],
    takeaways: [
      {
        metric: "Design at MEP stage",
        point:
          "Automation must be designed before electrical layouts are frozen — cabling, containment, panel space and the equipment room are the only decisions that cannot be retrofitted cheaply.",
        source: "Decide in this order",
        faq: "When should automation be designed into a project?",
      },
      {
        metric: "Wired backbone, wireless leaves",
        point:
          "Home-run Cat6A to every control point, keypad and sensor, with wireless used only at the leaves; wireless-first design degrades in concrete-and-steel construction.",
        source: "Cabling and containment benchmarks",
        faq: "Is wired or wireless automation better?",
      },
      {
        metric: "30% spare U-space and breaker capacity",
        point:
          "Allow 30% spare rack space and breaker capacity, 20% spare conduit fill, two cabling drops behind every display and a neutral at every switch box.",
        source: "Cabling and containment benchmarks",
        faq: "Is wired or wireless automation better?",
      },
      {
        metric: "Manual override, internet down",
        point:
          "Every automated function needs a manual override that works offline, with local processing for anything safety- or comfort-critical and cloud reserved for remote access and updates.",
        source: "Reliability rules",
        faq: "Does Matter remove the need for a wired backbone?",
      },
      {
        metric: "Client owns the programming files",
        point:
          "Programming files, admin credentials and licence records belong in the handover pack — without them the owner cannot change service partners without rebuilding the system.",
        source: "Commissioning and handover pack",
        faq: "Who should own the programming files?",
      },
    ],
    sections: [
      {
        heading: "Decide in this order",
        bullets: [
          "1. Scenes and daily operation — what the family or facility team actually does, hour by hour.",
          "2. Control surfaces — keypad, app, voice, panel, or scheduled logic. Voice is never the primary surface.",
          "3. Topology — wired backbone with wireless only at the leaves.",
          "4. Cabling and containment — the only irreversible decision on the list.",
          "5. Protocols and platform.",
          "6. Products.",
          "Most projects run this list backwards, starting with a brand, and then discover the cabling will not support it.",
        ],
      },
      {
        heading: "Protocol trade-offs",
        table: {
          columns: ["Protocol", "Strength", "Watch out for"],
          rows: [
            ["KNX", "Open, wired, decades of device support, no cloud dependency", "Needs a trained integrator and ETS discipline"],
            ["DALI-2", "The right answer for serious lighting control and tunable white", "Bus power supplies and addressing must be planned, not improvised"],
            ["BACnet/IP", "Standard for HVAC and building services integration", "Point naming discipline decides whether analytics is possible later"],
            ["Zigbee / Thread / Matter", "Good for retrofit and leaf devices", "Mesh reliability depends on mains-powered node density"],
            ["Proprietary ecosystems", "Fast, polished user experience", "Platform risk — verify local control survives an internet outage"],
          ],
        },
      },
      {
        heading: "Cabling and containment benchmarks",
        bullets: [
          "Home-run Cat6A to every control point, keypad and sensor location — wireless is a fallback, not a plan.",
          "A dedicated, ventilated equipment room or rack with a spare 30% U-space and 30% spare breaker capacity.",
          "Separate containment for control bus and mains, with 20% spare conduit fill for the second phase.",
          "Two structured cabling drops behind every display, not one.",
          "Neutral wire at every switch box — the most common retrofit blocker in Indian residences.",
          "Labelled, tested and certified links with a printed test report as a payment milestone.",
        ],
      },
      {
        heading: "Reliability rules",
        bullets: [
          "Every automated function must have a manual override that works with the internet down.",
          "Local processing for anything safety- or comfort-critical; cloud only for remote access and updates.",
          "One time source, one authentication directory, one logging destination.",
          "UPS the controllers, the network core and the gateway — not just the rack.",
          "No single-vendor lock on the network layer; keep switching and Wi-Fi independently serviceable.",
        ],
      },
      {
        heading: "Commissioning and handover pack",
        bullets: [
          "As-built schematics, panel schedules and IP address plan.",
          "Full device inventory with firmware versions and licence expiry dates.",
          "Scene documentation in plain language, one page per room.",
          "Admin credentials handed to the owner in writing — not retained solely by the integrator.",
          "Recorded walkthrough for the household staff or facility team.",
          "A defined AMC scope: response times, firmware policy, and who owns the programming files.",
        ],
      },
      {
        heading: "Failure patterns worth avoiding",
        bullets: [
          "Automation specified after electrical drawings are frozen.",
          "Wireless-first design in a concrete-and-steel structure.",
          "Programming files held hostage by the integrator.",
          "Scenes designed for a showroom demo rather than for a Tuesday morning.",
          "No documented rollback when a firmware update breaks a driver.",
        ],
      },
    ],
    faqs: [
      {
        q: "When should automation be designed into a project?",
        a: "At architectural and MEP stage, before electrical layouts are frozen. Cabling, containment, panel space and the equipment room are the decisions that cannot be retrofitted cheaply; product selection can wait.",
      },
      {
        q: "Is wired or wireless automation better?",
        a: "Wired backbone, wireless leaves. In Indian concrete-and-steel construction a wireless-first design tends to degrade as the home fills with furniture, appliances and neighbouring networks.",
      },
      {
        q: "Who should own the programming files?",
        a: "The client. Programming files, admin credentials and licence records belong in the handover pack. If an integrator retains them, the owner cannot change service partners without rebuilding the system.",
      },
      {
        q: "Does Matter remove the need for a wired backbone?",
        a: "No. Matter improves interoperability at the device layer; it does not change the physics of signal propagation, the need for local control during outages, or the value of home-run cabling.",
      },
    ],
    citation: "Arif Khan, “The Smart Automation Playbook”, arifkhanglobal.com, 2026.",
    related: [
      { to: "/solutions/smart-automation", label: "Smart Automation solution" },
      { to: "/for-architects", label: "For architects & design teams" },
      { to: "/ask-arif", label: "Ask Arif — answers by topic" },
    ],
  },

  {
    slug: "luxury-home-cinema-guide",
    metaTitle: "Luxury Home Cinema Design Guide | Reference",
    title: "The Luxury Home Cinema Guide",
    description:
      "A room-first guide to private cinema design — room ratios, sightlines, acoustic treatment, isolation, Dolby Atmos layouts, calibration targets and handover testing.",
    eyebrow: "Reference · Luxury Home Cinema",
    summary:
      "Private cinema performance is decided by the room, not the equipment list. This guide sets out the dimensional, acoustic and calibration benchmarks we hold projects to before a single speaker is chosen.",
    image: solCinema,
    updated: "2026-08-11",
    readTime: "13 min",
    keyFacts: [
      { label: "Budget rule of thumb", value: "Room & acoustics before electronics" },
      { label: "Design sequence", value: "Room → acoustics → speakers → screen → control" },
      { label: "Handover", value: "Measured calibration report, not a demo" },
    ],
    takeaways: [
      {
        metric: "Room before electronics",
        point:
          "Fund the room shell, isolation, HVAC and acoustic treatment before finalising equipment; electronics can be upgraded later, an untreated room cannot be without rebuilding it.",
        source: "Room first — always",
        faq: "How much of a home cinema budget should go to the room?",
      },
      {
        metric: "3.0 m finished ceiling",
        point:
          "A true Dolby Atmos overhead layer needs about 3.0 m finished ceiling height; below roughly 2.7 m the height layer stops reading as a separate layer.",
        source: "Dimensional and sightline benchmarks",
        faq: "What ceiling height does a Dolby Atmos home cinema need?",
      },
      {
        metric: "RT60 ≈ 0.25–0.35 s, NC 20–25",
        point:
          "Target a reverberation time of 0.25–0.35 s broadly flat from 125 Hz to 4 kHz with background noise at NC 20–25 — two numbers that outweigh any single component upgrade.",
        source: "Acoustic isolation and treatment targets",
        faq: "What reverberation time should a private cinema target?",
      },
      {
        metric: "1.5–2.0× screen width",
        point:
          "Set viewing distance at 1.5–2.0× screen width against actual seat rows, screen centre at seated eye level, and keep at least 1.0 m between the rear seats and the back wall.",
        source: "Dimensional and sightline benchmarks",
        faq: "What ceiling height does a Dolby Atmos home cinema need?",
      },
      {
        metric: "Multiple subwoofers, placed by measurement",
        point:
          "Several subwoofers positioned by measurement even out modal peaks and nulls across every seat in a way one larger corner subwoofer cannot.",
        source: "Immersive layout and calibration",
        faq: "Do I need more than one subwoofer?",
      },
    ],
    sections: [
      {
        heading: "Room first — always",
        paragraphs: [
          "The single largest determinant of how a private cinema sounds is the room's dimensions, construction and treatment. A modest speaker package in a correctly proportioned, isolated and treated room outperforms a flagship system in an untreated cube — every time, measurably.",
          "That is why the room shell, isolation strategy and treatment budget should be locked before the equipment schedule is written.",
        ],
      },
      {
        heading: "Dimensional and sightline benchmarks",
        table: {
          columns: ["Parameter", "Benchmark", "Note"],
          rows: [
            ["Room proportions", "Avoid whole-number ratios (e.g. 1:2:4)", "Reduces coincident room modes"],
            ["Minimum ceiling height", "3.0 m finished, for a true Atmos overhead layer", "Below 2.7 m, overhead effects collapse"],
            ["Viewing distance", "1.5–2.0× screen width for cinema-style immersion", "Tie to seat rows, not to wall length"],
            ["Eye height", "Screen centre at seated ear/eye level, first row", "Fixes the most common riser error"],
            ["Riser height", "Typically 150–450 mm per row, sightline-calculated", "Verify against rear-row head clearance"],
            ["Seat-to-rear-wall gap", "≥ 1.0 m", "Prevents bass boundary loading at the head position"],
            ["Screen-to-front-wall gap", "≥ 0.6 m for acoustically transparent screens", "Space for L/C/R and treatment"],
          ],
        },
      },
      {
        heading: "Acoustic isolation and treatment targets",
        table: {
          columns: ["Parameter", "Target", "Why"],
          rows: [
            ["Isolation from adjoining rooms", "Room-in-room construction, decoupled ceiling", "Bass is what escapes, and it escapes structurally"],
            ["Background noise (HVAC, NC)", "NC 20–25", "A quiet room raises usable dynamic range more than any amplifier"],
            ["Reverberation time (RT60)", "≈ 0.25–0.35 s, broadly flat 125 Hz–4 kHz", "Dialogue intelligibility and imaging"],
            ["Bass control", "Broadband absorption plus multiple-subwoofer placement", "Modal ringing, not level, is the usual problem"],
            ["First reflection points", "Treated on side walls, ceiling and floor", "Preserves centre-channel imaging"],
            ["HVAC design", "Low-velocity ducting, lined plenums, no exposed grilles in-line with seating", "Noise and draught at the head position"],
          ],
        },
      },
      {
        heading: "Immersive layout and calibration",
        bullets: [
          "Choose the channel layout from the room geometry and seat count, not from the receiver's maximum spec.",
          "Overhead speakers need a genuine, structurally supported ceiling plane — not a compromise into a beam.",
          "Multiple subwoofers, positioned by measurement, outperform one larger subwoofer in a corner.",
          "Calibrate by measurement (level, distance, crossover, target curve) and issue the before/after plots as a deliverable.",
          "Video: set peak white, gamma/EOTF and colour to the room's actual ambient light, with a printed calibration report.",
          "Lighting must be dimmable, glare-free and scene-linked; ambient light ruins contrast faster than a cheaper projector does.",
        ],
      },
      {
        heading: "Handover checklist",
        bullets: [
          "Measured acoustic report — RT60, background noise, frequency response at each seat.",
          "Video calibration report per source and per picture mode.",
          "One-button scenes tested by the household, not by the integrator.",
          "Spare lamp/light source, filters and consumables listed with lead times.",
          "Documented service access to every device without dismantling joinery.",
        ],
      },
    ],
    faqs: [
      {
        q: "How much of a home cinema budget should go to the room?",
        a: "Enough that the room shell, isolation, HVAC and acoustic treatment are fully funded before the electronics are finalised. Equipment can be upgraded later; a room that was never isolated or treated cannot be, without rebuilding it.",
      },
      {
        q: "What ceiling height does a Dolby Atmos home cinema need?",
        a: "Aim for a finished height of about 3.0 m so the overhead layer sits meaningfully above the listener. Below roughly 2.7 m the height layer stops being a separate layer and the immersive effect flattens.",
      },
      {
        q: "What reverberation time should a private cinema target?",
        a: "Broadly 0.25 to 0.35 seconds, reasonably flat from 125 Hz to 4 kHz, with a low background noise level of around NC 20–25. Those two numbers do more for perceived quality than any single component upgrade.",
      },
      {
        q: "Do I need more than one subwoofer?",
        a: "In most rooms, yes. Multiple subwoofers placed by measurement even out modal peaks and nulls across seats, which a single larger subwoofer in a corner cannot do.",
      },
    ],
    citation: "Arif Khan, “The Luxury Home Cinema Guide”, arifkhanglobal.com, 2026.",
    related: [
      { to: "/solutions/luxury-home-cinema", label: "Luxury Home Cinema solution" },
      { to: "/locations/home-cinema-consultant-delhi-ncr", label: "Home cinema consultant — Delhi NCR" },
      { to: "/projects", label: "Selected projects" },
    ],
  },

  {
    slug: "cctv-command-centre-standards",
    metaTitle: "CCTV & Command Centre Design Standards | Reference",
    title: "CCTV & Command Centre Design Standards",
    description:
      "Design standards for CCTV and security control rooms — pixel density targets, retention maths, VMS architecture, operator ergonomics, video wall sizing and SOP design.",
    eyebrow: "Reference · Security Infrastructure",
    summary:
      "Camera count is the wrong specification. This reference sets out the pixel-density, retention, network and operator-workload standards that decide whether a control room produces evidence or just storage.",
    image: solCctv,
    updated: "2026-08-11",
    readTime: "12 min",
    keyFacts: [
      { label: "Specify by", value: "Pixel density, not camera count" },
      { label: "Operator load", value: "Alarms per hour, not screens per wall" },
      { label: "Deliverable", value: "Coverage drawings + retention calculation" },
    ],
    takeaways: [
      {
        metric: "~250 px/m for identification",
        point:
          "Identification-grade coverage needs about 250 pixels per metre; recognition about 125 px/m, observation 80 px/m, detection 40 px/m and general monitoring 25 px/m.",
        source: "Specify coverage, not cameras",
        faq: "What pixel density is required for identification-grade CCTV?",
      },
      {
        metric: "Retention in days, not terabytes",
        point:
          "State storage as retention days at a defined resolution, frame rate and codec, sized on realistic scene complexity with headroom for the phase-two camera count.",
        source: "Retention and storage maths",
        faq: "How should CCTV storage be specified?",
      },
      {
        metric: "2–3 desk monitors per operator",
        point:
          "Give each operator two to three desk monitors and design for fewer than ten actionable alarms per hour; more screens and more alarms both reduce assessment quality.",
        source: "Command centre ergonomics",
        faq: "How many monitors should a control-room operator have?",
      },
      {
        metric: "150–300 lux, NC 30–35",
        point:
          "Control rooms need indirect ambient light of 150–300 lux with no screen reflections, absorptive finishes at NC 30–35, and break rotation on continuous monitoring shifts.",
        source: "Command centre ergonomics",
        faq: "What makes a command centre operationally successful?",
      },
      {
        metric: "Every alarm maps to a written response",
        point:
          "Operational success is measured: alarms by type, nuisance rate, mean time to assess and resolve, quarterly camera-health audits and an AMC tied to availability.",
        source: "SOPs and measurable operations",
        faq: "What makes a command centre operationally successful?",
      },
    ],
    sections: [
      {
        heading: "Specify coverage, not cameras",
        paragraphs: [
          "A tender that says “96 cameras” specifies a budget, not a security outcome. A tender that says “identification-grade coverage at every entry point and monitoring-grade coverage across the car park” specifies an outcome any competent integrator can price and any consultant can verify.",
        ],
        table: {
          caption: "Pixel-density bands used for coverage drawings (aligned with common industry practice).",
          columns: ["Purpose", "Target pixel density", "Typical use"],
          rows: [
            ["Monitoring", "~25 px/m", "Crowd flow, general area awareness"],
            ["Detection", "~40 px/m", "Presence of a person in a zone"],
            ["Observation", "~80 px/m", "Behaviour, clothing, direction"],
            ["Recognition", "~125 px/m", "Recognising a known individual"],
            ["Identification", "~250 px/m", "Evidential identification of a stranger"],
            ["Licence plate (ANPR)", "~150–200 px/m within the capture zone", "Lane-specific, with angle and speed limits"],
          ],
        },
      },
      {
        heading: "Retention and storage maths",
        bullets: [
          "State retention in days at a defined frame rate, resolution and codec — not in terabytes.",
          "Size storage on realistic scene complexity; smart-codec savings collapse on moving foliage and rain.",
          "Add headroom for the phase-two camera count that everyone knows is coming.",
          "Specify RAID level, rebuild time and failure behaviour, not just capacity.",
          "Confirm the export workflow produces court-admissible, watermarked evidence with an audit trail.",
        ],
      },
      {
        heading: "Network and VMS architecture",
        bullets: [
          "Separate, VLAN-segmented surveillance network with no unmanaged switches in the path.",
          "Multicast or dual-stream design so the video wall does not saturate the recorder's uplink.",
          "Hardened VMS: unique credentials per operator, role-based access, no shared admin login.",
          "Time synchronisation from a single trusted source — evidence hinges on timestamps.",
          "Documented failover: what happens to recording when the head end, the link or the power dies.",
          "Analytics licensed with GPU headroom for the next phase, not exactly to today's channel count.",
        ],
      },
      {
        heading: "Command centre ergonomics",
        table: {
          columns: ["Parameter", "Benchmark", "Note"],
          rows: [
            ["Operator viewing distance to video wall", "Screen diagonal × 4–6 for situational awareness", "Sized after content, not after wall width"],
            ["Desk monitors per operator", "2–3 maximum", "More screens reduce, not increase, alarm handling"],
            ["Sustained alarms per operator", "Design under 10 actionable alarms per hour", "Above this, assessment quality drops sharply"],
            ["Ambient lighting", "150–300 lux, indirect, no reflections on screens", "Control rooms are lit wrong more often than not"],
            ["Acoustics", "Absorptive finishes; NC 30–35", "Shift-long fatigue is an operational risk"],
            ["Shift design", "Break rotation for continuous monitoring roles", "Attention degrades well before the shift ends"],
          ],
        },
      },
      {
        heading: "SOPs and measurable operations",
        bullets: [
          "Every alarm type maps to a written response, an escalation path and a closure code.",
          "Monthly report: alarms by type, nuisance rate, mean time to assess, mean time to resolve.",
          "Quarterly camera-health audit — focus, obstruction, IR, retention verification.",
          "Annual tabletop exercise with the response team and estate management.",
          "AMC tied to system availability and health-audit results, not just to visit counts.",
        ],
      },
    ],
    faqs: [
      {
        q: "What pixel density is required for identification-grade CCTV?",
        a: "About 250 pixels per metre across the target's width within the capture zone. Recognition of a known person needs roughly 125 px/m, observation about 80 px/m and general monitoring around 25 px/m.",
      },
      {
        q: "How should CCTV storage be specified?",
        a: "In retention days at a stated resolution, frame rate and codec, sized on realistic scene complexity with headroom for the next phase — never as a bare terabyte figure, which hides all the assumptions that matter.",
      },
      {
        q: "How many monitors should a control-room operator have?",
        a: "Two to three. Beyond that, alarm handling quality falls. The video wall is for shared situational awareness; the operator works from the desk.",
      },
      {
        q: "What makes a command centre operationally successful?",
        a: "Written SOPs per alarm type, a nuisance-alarm rate low enough that operators still assess every event, measured mean time to assess, and an AMC contract tied to availability rather than to visit counts.",
      },
    ],
    citation: "Arif Khan, “CCTV & Command Centre Design Standards”, arifkhanglobal.com, 2026.",
    related: [
      { to: "/solutions/cctv-command-centre", label: "CCTV & Command Centre solution" },
      { to: "/solutions/security-infrastructure", label: "Security Infrastructure" },
      { to: "/resources/perimeter-protection-benchmarks", label: "Perimeter protection benchmarks" },
    ],
  },

  {
    slug: "auditorium-av-specification-checklist",
    metaTitle: "Auditorium AV Specification Checklist | Reference",
    title: "Auditorium & Enterprise AV Specification Checklist",
    description:
      "A specification checklist for auditoriums, boardrooms and training halls — acoustics, speech intelligibility targets, sightlines, display sizing, control and acceptance testing.",
    eyebrow: "Reference · Audio Video",
    summary:
      "Auditorium AV fails in predictable ways: a room too reverberant for speech, a display too small for the back row, and a control system nobody on staff can operate. This checklist prevents all three.",
    image: solAuditorium,
    updated: "2026-08-11",
    readTime: "10 min",
    keyFacts: [
      { label: "Primary metric", value: "Speech intelligibility (STI)" },
      { label: "Sizing rule", value: "Read from the last row, not the first" },
      { label: "Acceptance", value: "Measured, witnessed, documented" },
    ],
    takeaways: [
      {
        metric: "STI ≥ 0.60",
        point:
          "Design for a speech transmission index of 0.60 or better across the seating area, with 0.50 as the absolute floor — intelligibility is what an audience actually experiences.",
        source: "Acoustics before equipment",
        faq: "What speech intelligibility target should an auditorium meet?",
      },
      {
        metric: "RT 0.6–0.9 s, NC 25–30",
        point:
          "Speech-led halls want a reverberation time of roughly 0.6–0.9 s with HVAC background noise at NC 25–30, and rear-wall slap echo treated before loudspeaker power is added.",
        source: "Acoustics before equipment",
        faq: "What speech intelligibility target should an auditorium meet?",
      },
      {
        metric: "≤ 4× image height",
        point:
          "Size displays from the farthest seat: within about 4× image height for detailed content, 6–8× for basic content, with the smallest text at least 1/200 of the farthest viewing distance.",
        source: "Sightlines and display sizing",
        faq: "How large should an auditorium display be?",
      },
      {
        metric: "±45° viewing cone, 1.2 m image base",
        point:
          "Keep every seat within ±45° of screen centre and the bottom of the image at least 1.2 m above floor level, verified against rear-row head heights.",
        source: "Sightlines and display sizing",
        faq: "Projector or LED wall for a hall with daylight?",
      },
      {
        metric: "±3 dB measured coverage",
        point:
          "Acceptance is measured, witnessed and documented: ±3 dB coverage and STI plots, a last-row legibility check, and a full rehearsal of every event format with client staff operating.",
        source: "Audio system checklist",
        faq: "What should be included in AV acceptance testing?",
      },
    ],
    sections: [
      {
        heading: "Acoustics before equipment",
        bullets: [
          "Target speech transmission index (STI) of 0.60 or better across the seating area; 0.50 is the floor for acceptability.",
          "Reverberation time appropriate to use: roughly 0.6–0.9 s for speech-led halls, longer only where music performance is a stated requirement.",
          "Background noise from HVAC at NC 25–30 in the audience area.",
          "Treat rear-wall slap echo and parallel side walls before adding loudspeaker power.",
          "Confirm the acoustic scope has a named owner — it is the item most often deleted at value engineering.",
        ],
      },
      {
        heading: "Sightlines and display sizing",
        table: {
          columns: ["Parameter", "Benchmark", "Note"],
          rows: [
            ["Farthest viewer distance (detailed content)", "≤ 4× image height", "Spreadsheets, CAD, dense slides"],
            ["Farthest viewer distance (basic content)", "≤ 6–8× image height", "Video, large-type presentations"],
            ["Minimum text height", "1/200 of the farthest viewing distance", "Test with real slides, not a test pattern"],
            ["Bottom of image above floor", "≥ 1.2 m, verified against rear-row heads", "The most common retrofit complaint"],
            ["Horizontal viewing cone", "Within ±45° of screen centre", "Seats outside this are effectively audio-only"],
            ["Ambient light on screen", "Controlled; specify projector/LED to measured lux", "Glass-walled halls need LED, not lumens"],
          ],
        },
      },
      {
        heading: "Audio system checklist",
        bullets: [
          "Even coverage: ±3 dB across the seating area, verified by measurement.",
          "Headroom above the design SPL so the system never runs into limiting during a live event.",
          "Assistive listening provision where the brief or local accessibility policy requires it.",
          "Microphone plan tied to the actual event formats — panel, lectern, roving, hybrid.",
          "Feedback stability margin tested with the real microphone positions, not on a bench.",
          "DSP configuration files documented and handed over.",
        ],
      },
      {
        heading: "Control, hybrid and operations",
        bullets: [
          "One-touch presets for the three formats staff run weekly; everything else behind an advanced page.",
          "Hybrid meeting path tested with the organisation's actual conferencing platform.",
          "Camera framing presets tuned to the lectern and panel positions.",
          "Remote monitoring and asset management for multi-room estates.",
          "Named staff trained, with a laminated one-page operating card in the rack room.",
        ],
      },
      {
        heading: "Acceptance testing",
        bullets: [
          "Measured STI and SPL coverage plots across the seating area.",
          "Display uniformity, contrast and text-legibility check from the last row.",
          "Full end-to-end rehearsal of each event format with client staff operating.",
          "Signed as-builts, rack elevations, cable schedules and configuration backups.",
          "Warranty, spares and response-time terms agreed in writing before final payment.",
        ],
      },
    ],
    faqs: [
      {
        q: "What speech intelligibility target should an auditorium meet?",
        a: "Design for an STI of 0.60 or better across the seating area, with 0.50 as the minimum acceptable floor. Intelligibility, not loudspeaker brand, is what audiences actually experience.",
      },
      {
        q: "How large should an auditorium display be?",
        a: "Size it from the farthest seat: within about 4× image height for detailed content and 6–8× for basic content, with the smallest on-screen text at least 1/200 of the farthest viewing distance.",
      },
      {
        q: "Projector or LED wall for a hall with daylight?",
        a: "In a glazed, daylit hall, direct-view LED is usually the correct answer. Beyond a point, adding projector lumens costs more and delivers less contrast than an LED wall sized to the room.",
      },
      {
        q: "What should be included in AV acceptance testing?",
        a: "Measured intelligibility and coverage plots, a legibility check from the last row, a full rehearsal of every event format with client staff at the controls, and a documentation pack including configuration backups.",
      },
    ],
    citation:
      "Arif Khan, “Auditorium & Enterprise AV Specification Checklist”, arifkhanglobal.com, 2026.",
    related: [
      { to: "/solutions/auditorium-av", label: "Auditorium AV solution" },
      { to: "/locations/auditorium-av-consultant-delhi-ncr", label: "Auditorium AV consultant — Delhi NCR" },
      { to: "/for-architects", label: "For architects & design teams" },
    ],
  },
];

export const RESOURCE_SLUGS = RESOURCES.map((r) => r.slug);

export function getResource(slug: string): Resource | undefined {
  return RESOURCES.find((r) => r.slug === slug);
}
